import { ValidationErrorEnum } from 'components/Core/WalletForms/validations/src/ValidationErrorEnum';
import {
  HTTP_STATUS_CODES,
  NODE_NETWORK_ERRORS,
  PAYMENT_ERROR_TYPES,
  TXN_MAP,
  CP_ERROR_CODES,
} from 'types/constants';

/**
 * Translates ValidationErrorEnum type in to a string that belongs in
 * our i18n keys.
 * @param err
 */
export const formErrorTranslator =
  (isInNameValidationExperiment = false) =>
  (err) => {
    switch (err) {
      case ValidationErrorEnum.REQUIRED_ERROR_MESSAGE: {
        return 'PAYFLOW_BLANK_FIELD';
      }

      case ValidationErrorEnum.INVALID_ACCOUNT_NUMBER: {
        return 'PAYFLOW_VALIDATION_BANK_ACCOUNT_NUMBER_LENGTH';
      }

      case ValidationErrorEnum.INVALID_ACCOUNT_NUMBER_CA: {
        return 'PAYFLOW_VALIDATION_BANK_ACCOUNT_NUMBER_LENGTH_CA';
      }

      case ValidationErrorEnum.ACCOUNT_CONFIRMATION_MISMATCH: {
        return 'PAYFLOW_VALIDATION_BANK_CONFIRMACCOUNTNUMBER_MATCH';
      }

      case ValidationErrorEnum.INVALID_FIRST_AND_LAST_NAME: {
        return isInNameValidationExperiment
          ? 'PAYFLOW_VALIDATION_CARD_INVALID_FIRST_AND_LAST_NAME_EXTENDED'
          : 'PAYFLOW_VALIDATION_CARD_INVALID_FIRST_AND_LAST_NAME';
      }

      case ValidationErrorEnum.INVALID_ROUTING_NUMBER: {
        return 'PAYFLOW_VALIDATION_BANK_ROUTINGNUMBER_LENGTH';
      }

      case ValidationErrorEnum.INVALID_CARD_NUMBER: {
        return 'PAYFLOW_VALIDATION_CARD_CARDNUMBER_LENGTH';
      }

      case ValidationErrorEnum.INVALID_CVC: {
        return 'PAYFLOW_VALIDATION_CARD_CVV_NONAMEX_LENGTH';
      }

      case ValidationErrorEnum.INVALID_CVC_AMEX: {
        return 'PAYFLOW_VALIDATION_CARD_CVV_AMEX_LENGTH';
      }

      case ValidationErrorEnum.INVALID_CVC_NON_AMEX: {
        return 'PAYFLOW_VALIDATION_CARD_CVV_NONAMEX_LENGTH';
      }

      case ValidationErrorEnum.INVALID_EXP_DATE: {
        return 'PAYFLOW_VALIDATION_CARD_EXPDATE_INVALID';
      }

      case ValidationErrorEnum.INVALID_ZIP_CODE: {
        return 'PAYFLOW_VALIDATION_CARD_ZIP_INVALID';
      }

      case ValidationErrorEnum.INVALID_CARD_TYPE: {
        return 'PAYFLOW_VALIDATION_CARD_TYPE_INVALID';
      }

      case ValidationErrorEnum.INVALID_TRANSIT_NUMBER: {
        return 'PAYFLOW_VALIDATION_BANK_TRANSIT_NUMBER_LENGTH';
      }

      case ValidationErrorEnum.INVALID_INSTITUTION_NUMBER: {
        return 'PAYFLOW_VALIDATION_BANK_INSTITUTION_NUMBER_LENGTH';
      }

      case ValidationErrorEnum.INVALID_STREET_ADDRESS: {
        return 'PAYFLOW_VALIDATION_BILLING_STREET_INVALID';
      }

      case ValidationErrorEnum.INVALID_CITY: {
        return 'PAYFLOW_VALIDATION_BILLING_CITY_INVALID';
      }

      case ValidationErrorEnum.INVALID_STATE: {
        return 'PAYFLOW_VALIDATION_BILLING_STATE_INVALID';
      }

      default: {
        return 'PAYFLOW_BLANK_FIELD';
      }
    }
  };

/**
 *
 * @param status
 */
export const networkPaymentErrorTranslator = ({ status, data, featureFlags = {} } = {}) => {
  if (data && data.message) {
    return data;
  }

  if (
    status === HTTP_STATUS_CODES.Ok &&
    data &&
    data.paymentStatus === PAYMENT_ERROR_TYPES.DECLINED
  ) {
    // https://featureflags-e2e.app.intuit.com/app/ixp-ui/feature-flags/e2e/SBSEG-CP-credit-floor-pm/version/latest
    // https://featureflags-e2e.app.intuit.com/app/ixp-ui/feature-flags/e2e/SBSEG-CP-partial-payment-workflows/version/latest
    // Feature flag to control whether or not user will get new reasonCode responses
    if (
      featureFlags['SBSEG-CP-credit-floor-pm'] ||
      featureFlags['SBSEG-CP-partial-payment-workflows'] ||
      featureFlags['SBSEG-CP-billing-address-enabled']
    ) {
      // reasonCode is nested in outcome object from SCS response
      const reasonCode = data.outcome?.reasonCode;
      return {
        message: PAYMENT_ERROR_TYPES.DECLINED,
        ...(reasonCode && { reasonCode }),
      };
    }
    return {
      message: PAYMENT_ERROR_TYPES.DECLINED,
    };
  }
  if (
    featureFlags['payment-error-refresh-experience'] &&
    (status === NODE_NETWORK_ERRORS.ECONNABORTED || status === HTTP_STATUS_CODES.BadGateway)
  ) {
    return { message: PAYMENT_ERROR_TYPES.TIMEOUT };
  }
  if (
    featureFlags['appconnect-error-refresh-experience'] &&
    (data.paymentStatus === TXN_MAP.STATUS.DECLINED || data.paymentStatus === TXN_MAP.STATUS.ERROR)
  ) {
    return { message: PAYMENT_ERROR_TYPES.APPCONNECT };
  }
  if (status >= HTTP_STATUS_CODES.InternalServerError) {
    return { message: PAYMENT_ERROR_TYPES.GENERAL_PAYMENT_ERROR };
  }

  if (status === HTTP_STATUS_CODES.BadRequest) {
    const { errorCode } = data;
    if (errorCode === CP_ERROR_CODES.RISK_PAYMENT_ERROR) {
      return { message: PAYMENT_ERROR_TYPES.RISK_PAYMENT_ERROR };
    }
    if (errorCode === CP_ERROR_CODES.SALE_VALIDATION) {
      return { message: PAYMENT_ERROR_TYPES.SALE_VALIDATION };
    }
    if (errorCode === CP_ERROR_CODES.MISSING_ZIPCODE_ERROR) {
      return { message: PAYMENT_ERROR_TYPES.MISSING_ZIPCODE_ERROR };
    }
  }

  return { message: PAYMENT_ERROR_TYPES.GENERAL_PAYMENT_ERROR };
};
